import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import './DraggableAvatarCanvas.css';
// Import all avatars
import avatarCat from '../assets/avatars/avatar-cat.png';
import avatarDog from '../assets/avatars/avatar-dog.png';
import avatarFox from '../assets/avatars/avatar-fox.png';
import avatarOwl from '../assets/avatars/avatar-owl.png';
import avatarRabbit from '../assets/avatars/avatar-rabbit.png';
import linkedinLogo from '../assets/platform/linkedin.png';
import xLogo from '../assets/platform/X.png';
import insLogo from '../assets/platform/ins.png';

// Create an array of available avatars
const avatarsList = [avatarCat, avatarDog, avatarFox, avatarOwl, avatarRabbit];
const getRandomAvatar = () => avatarsList[Math.floor(Math.random() * avatarsList.length)];

const DOT_SPACING = 32; // px
const DOT_RADIUS = 2.2; // px
const DOT_COLOR = 'rgba(41, 121, 255, 0.10)';
const AVATAR_SIZE = 120; // px
const CONNECTION_LINE_COLOR = 'rgba(41, 121, 255, 0.30)';
const Y_OFFSET = 100; // px, vertical offset for connection lines
const LOADING_MESSAGES = [
  "Searching through our multi-agent network...",
  "Analyzing potential connections...",
  "Simulating agent interactions...",
  "Evaluating profile matches...",
  "Generating personalized recommendations..."
];

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

function getGridDots(width, height) {
  const dots = [];
  for (let x = DOT_SPACING / 2; x < width; x += DOT_SPACING) {
    for (let y = DOT_SPACING / 2; y < height; y += DOT_SPACING) {
      dots.push({ x, y });
    }
  }
  return dots;
}

// Helper to get platform logo and alt text
const getPlatformLogo = (platform) => {
  if (!platform) return null;
  if (platform.toLowerCase().includes('linkedin')) return { logo: linkedinLogo, alt: 'LinkedIn' };
  if (platform.toLowerCase().includes('twitter') || platform.toLowerCase().includes('x')) return { logo: xLogo, alt: 'Twitter/X' };
  if (platform.toLowerCase().includes('instagram')) return { logo: insLogo, alt: 'Instagram' };
  return null;
};

const DraggableAvatarCanvas = ({ avatar, name, profileData }) => {
  const canvasRef = useRef(null);
  const [avatarLifted, setAvatarLifted] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showChatBox, setShowChatBox] = useState(true);
  const [showProfileInfo, setShowProfileInfo] = useState(false);
  const [chat, setChat] = useState([
    { from: 'agent', text: '' }
  ]);
  const [input, setInput] = useState('');
  const [canvasSize, setCanvasSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [serverStatus, setServerStatus] = useState('checking');
  const [retryCount, setRetryCount] = useState(0);
  const [searchResults, setSearchResults] = useState([]);                                        
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [animationFrame, setAnimationFrame] = useState(0);
  const animationFrameRef = useRef(null);
  const lastTimeRef = useRef(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000; // 2 seconds
  const [selectedProfile, setSelectedProfile] = useState(null);

  // Animation loop
  useLayoutEffect(() => {
    const animate = (timestamp) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const deltaTime = timestamp - lastTimeRef.current;
      
      if (deltaTime > 16) { // ~60fps
        setRotation(prev => (prev + 0.01) % (2 * Math.PI));
        setAnimationFrame(prev => prev + 1);
        lastTimeRef.current = timestamp;
      }
      
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    animationFrameRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Check server status on component mount
  useEffect(() => {
    checkServerHealth();
  }, []);

  // Update loading messages
  useEffect(() => {
    if (showLoadingScreen) {
      const interval = setInterval(() => {
        setLoadingMessageIndex(prev => (prev + 1) % LOADING_MESSAGES.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [showLoadingScreen]);

  // Update loading progress
  useEffect(() => {
    if (showLoadingScreen && loadingProgress < 100) {
      const interval = setInterval(() => {
        setLoadingProgress(prev => Math.min(prev + 1, 100));
      }, 50);
      return () => clearInterval(interval);
    }
  }, [showLoadingScreen, loadingProgress]);

  // Assign a random avatar to each search result if not present
  useEffect(() => {
    setSearchResults(prevResults =>
      prevResults.map(result => {
        if (!result.profile.avatar) {
          return {
            ...result,
            profile: {
              ...result.profile,
              avatar: getRandomAvatar()
            }
          };
        }
        return result;
      })
    );
  }, [searchResults.length]);

  const checkServerHealth = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/health`);
      const data = await response.json();
      setServerStatus(data.status === 'healthy' ? 'healthy' : 'unhealthy');
    } catch (error) {
      setServerStatus('unavailable');
      console.error('Server health check failed:', error);
    }
  };

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const fetchWithRetry = async (url, options, retries = MAX_RETRIES) => {
    try {
      // Check server status before making request
      if (serverStatus !== 'healthy') {
        await checkServerHealth();
        if (serverStatus !== 'healthy') {
          throw new Error('Server is not available');
        }
      }

      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      if (retries > 0) {
        setStatusMessage(`Connection error. Retrying in ${RETRY_DELAY/1000} seconds... (${retries} attempts left)`);
        await sleep(RETRY_DELAY);
        return fetchWithRetry(url, options, retries - 1);
      }
      throw error;
    }
  };

  // Center the avatar on mount and update grid on resize
  useEffect(() => {
    const updateCanvasSize = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        setCanvasSize({ width: rect.width, height: rect.height });
      }
    };
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  // Animate avatar up and show chat
  useEffect(() => {
    setTimeout(() => setAvatarLifted(true), 400);
    setTimeout(() => setShowChat(true), 900);
  }, []);

  // Update the agent intro message if name changes
  useEffect(() => {
    setChat(prev => {
      const intro = { from: 'agent', text: `Hi, I'm ${name || 'your agent'}, another you. What can I help you network today?` };
      if (prev.length === 0) return [intro];
      if (prev[0].from === 'agent') {
        return [intro, ...prev.slice(1)];
      }
      return [intro, ...prev];
    });
  }, [name]);

  const handleAvatarClick = () => {
    setShowProfileInfo(!showProfileInfo);
  };

  const handleInputChange = (e) => setInput(e.target.value);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    
    if (serverStatus !== 'healthy') {
      setChat(prev => [...prev, { 
        from: 'agent', 
        text: "The server is not available. Please make sure the backend server is running and try again." 
      }]);
      setStatusMessage('Server unavailable');
      return;
    }
    
    setChat(prev => [...prev, { from: 'user', text: input }]);
    setIsLoading(true);
    setRetryCount(0);
    setShowLoadingScreen(true);
    setLoadingProgress(0);
    
    try {
      const data = await fetchWithRetry(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          query: input,
          user_profile: profileData || {}
        })
      });
      
      if (data.success) {
        setSearchResults(data.profiles || []);
        
        let responseText = "";
        if (data.message) {
          responseText += data.message + "\n\n";
        }
        
        if (data.profiles && data.profiles.length > 0) {
          data.profiles.forEach((profile, index) => {
            responseText += `${index + 1}. ${profile.profile.name} (${profile.profile.headline || 'No headline'})\n\n`;
            
            if (profile.analysis) {
              try {
                console.log('Raw profile.analysis:', profile.analysis);
                console.log('Type of profile.analysis:', typeof profile.analysis);
                
                // Use the analysis object directly
                const analysis = profile.analysis;
                console.log('Analysis object:', analysis);

                // Format the analysis sections
                let formattedAnalysis = "Analysis\n\n";

                if (analysis.match_reasons && analysis.match_reasons.length > 0) {
                  formattedAnalysis += "🎯 Match Reasons\n";
                  analysis.match_reasons.forEach(reason => {
                    formattedAnalysis += `${reason}\n`;
                  });
                  formattedAnalysis += "\n";
                }

                if (analysis.commonalities && analysis.commonalities.length > 0) {
                  formattedAnalysis += "🤝 Commonalities\n";
                  analysis.commonalities.forEach(common => {
                    formattedAnalysis += `${common}\n`;
                  });
                  formattedAnalysis += "\n";
                }

                if (analysis.value_propositions && analysis.value_propositions.length > 0) {
                  formattedAnalysis += "💡 Value Propositions\n";
                  analysis.value_propositions.forEach(value => {
                    formattedAnalysis += `${value}\n`;
                  });
                  formattedAnalysis += "\n";
                }

                if (analysis.opening_message) {
                  formattedAnalysis += "Suggested Opening Message\n";
                  formattedAnalysis += `${analysis.opening_message}\n\n`;
                }

                responseText += formattedAnalysis;
              } catch (error) {
                console.error('Error parsing analysis:', error);
                console.log('Raw analysis content:', profile.analysis);
                responseText += "Error parsing analysis data.\n";
              }
            }
            responseText += "\n";
          });
        } else {
          responseText += "No matching profiles found. Try adjusting your search criteria.";
        }
        
        setChat(prev => [...prev, { from: 'agent', text: responseText }]);
        setStatusMessage('');
      } else {
        let errorMessage = data.message || "Sorry, I couldn't process your request.";
        setChat(prev => [...prev, { from: 'agent', text: errorMessage }]);
        setStatusMessage('Search failed. Please try again.');
      }
    } catch (error) {
      console.error('Error in handleSend:', error);
      const errorMessage = retryCount < MAX_RETRIES 
        ? "Connection error. Retrying..."
        : serverStatus === 'unavailable'
          ? "The server is not available. Please make sure the backend server is running and try again."
          : "Unable to connect to the server. Please check your internet connection and try again later.";
      
      setChat(prev => [...prev, { from: 'agent', text: errorMessage }]);
      
      if (retryCount < MAX_RETRIES) {
        setRetryCount(prev => prev + 1);
        setStatusMessage(`Retrying in ${RETRY_DELAY/1000} seconds... (${MAX_RETRIES - retryCount} attempts left)`);
      } else {
        setStatusMessage('Connection failed. Please try again later.');
      }
    } finally {
      setIsLoading(false);
      setInput('');
      setShowLoadingScreen(false);
      setLoadingProgress(0);
    }
  };

  const dots = getGridDots(canvasSize.width, canvasSize.height);

  // Add a vertical offset (e.g., 80px or 100px)
  const verticalOffset = 180; // Increased to move avatar further below center
  const centerY = canvasSize.height / 2 + verticalOffset - (avatarLifted ? 100 : 0);
  const orbitRadius = 180; // Distance from center to orbiting avatars

  // Calculate positions for search result avatars in a circle
  const orbitCenterX = canvasSize.width / 2;
  const orbitCenterY = centerY - Y_OFFSET;
  const resultAvatars = searchResults.map((result, index) => {
    const angle = rotation + (2 * Math.PI * index) / searchResults.length;
    const x = orbitCenterX + orbitRadius * Math.cos(angle);
    const y = orbitCenterY + orbitRadius * Math.sin(angle);
    return {
      ...result,
      position: { x, y }
    };
  });

  return (
    <div className="draggable-canvas" ref={canvasRef}>
      <svg className="canvas-dots-bg" width={canvasSize.width} height={canvasSize.height} style={{position:'absolute',top:0,left:0,zIndex:1}}>
        {dots.map((dot, i) => (
          <circle key={i} cx={dot.x} cy={dot.y} r={DOT_RADIUS} fill={DOT_COLOR} />
        ))}
        
        {/* Draw connection lines */}
        {resultAvatars.map((result, index) => {
          const userAvatarCenterX = canvasSize.width / 2;
          const userAvatarCenterY = centerY - Y_OFFSET;
          const resultAvatarCenterX = result.position.x;
          const resultAvatarCenterY = result.position.y;

          return (
            <line
              key={`line-${index}`}
              x1={userAvatarCenterX}
              y1={userAvatarCenterY}
              x2={resultAvatarCenterX}
              y2={resultAvatarCenterY}
              stroke={CONNECTION_LINE_COLOR}
              strokeWidth="2"
              strokeDasharray="5,5"
            />
          );
        })}

        {/* Debug dots at avatar centers */}
        {resultAvatars.map((result, index) => (
          <circle
            key={`debug-dot-${index}`}
            cx={result.position.x}
            cy={result.position.y}
            r={3}
            fill="red"
          />
        ))}

        {/* Orbiting avatars as SVG images */}
        {resultAvatars.map((result, index) => (
          <g key={`svg-avatar-${index}`}> 
            <image
              href={result.profile.avatar}
              x={result.position.x - AVATAR_SIZE / 2}
              y={result.position.y - AVATAR_SIZE / 2}
              width={AVATAR_SIZE}
              height={AVATAR_SIZE}
              style={{
                borderRadius: '50%',
                filter: 'drop-shadow(0 0 10px rgba(41, 121, 255, 0.3))'
              }}
            />
            <circle
              cx={result.position.x}
              cy={result.position.y}
              r={AVATAR_SIZE / 2}
              fill="transparent"
              style={{ cursor: 'pointer', pointerEvents: 'all' }}
              onClick={() => {
                setShowProfileInfo(true);
                setSelectedProfile(result);
              }}
            />
          </g>
        ))}
      </svg>
      
      {/* User's avatar */}
      <img
        src={avatar}
        alt="avatar"
        className={`draggable-avatar${avatarLifted ? ' lifted' : ''}`}
        style={{ 
          left: canvasSize.width / 2 - AVATAR_SIZE / 2, 
          top: centerY - AVATAR_SIZE / 2, 
          transition: avatarLifted ? 'top 0.5s cubic-bezier(.4,1.6,.4,1)' : 'top 0.4s' 
        }}
        onClick={handleAvatarClick}
        draggable={false}
      />
      
      {showProfileInfo && selectedProfile && (
        <div className="profile-info-modal" onClick={() => setShowProfileInfo(false)}>
          <div className="profile-info-content" onClick={e => e.stopPropagation()}>
            <button 
              className="close-profile-button"
              onClick={() => setShowProfileInfo(false)}
            >
              ×
            </button>
            <div className="profile-header">
              <img 
                src={selectedProfile.profile.avatar || 'default-avatar.png'} 
                alt="profile" 
                className="profile-avatar"
              />
              <div className="profile-basic-info">
                <h3>{selectedProfile.profile.name}</h3>
                <p className="profile-headline">{selectedProfile.profile.headline}</p>
                {/* Platform icon and link */}
                {selectedProfile.profile.profile_url && selectedProfile.profile.platform && (() => {
                  const platform = getPlatformLogo(selectedProfile.profile.platform);
                  return platform ? (
                    <a
                      href={selectedProfile.profile.profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', marginTop: 8 }}
                    >
                      <img
                        src={platform.logo}
                        alt={platform.alt}
                        style={{ width: 28, height: 28, verticalAlign: 'middle', borderRadius: 4, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
                      />
                    </a>
                  ) : null;
                })()}
              </div>
            </div>
            
            {selectedProfile.analysis && (
              <div className="analysis-section">
                <h4>Analysis</h4>
                
                {selectedProfile.analysis.match_reasons && selectedProfile.analysis.match_reasons.length > 0 && (
                  <div className="analysis-subsection">
                    <h5>🎯 Match Reasons</h5>
                    <ul>
                      {selectedProfile.analysis.match_reasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedProfile.analysis.commonalities && selectedProfile.analysis.commonalities.length > 0 && (
                  <div className="analysis-subsection">
                    <h5>🤝 Commonalities</h5>
                    <ul>
                      {selectedProfile.analysis.commonalities.map((common, idx) => (
                        <li key={idx}>{common}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedProfile.analysis.value_propositions && selectedProfile.analysis.value_propositions.length > 0 && (
                  <div className="analysis-subsection">
                    <h5>💡 Value Propositions</h5>
                    <ul>
                      {selectedProfile.analysis.value_propositions.map((value, idx) => (
                        <li key={idx}>{value}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedProfile.analysis.opening_message && (
                  <div className="analysis-subsection">
                    <h5>✉️ Suggested Opening Message</h5>
                    <div className="opening-message-container">
                      <p className="opening-message">{selectedProfile.analysis.opening_message}</p>
                      <button className="copy-message-btn" onClick={() => {
                        navigator.clipboard.writeText(selectedProfile.analysis.opening_message);
                      }}>
                        Copy Message
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showLoadingScreen && (
        <div className="loading-screen">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <h2>Searching Network</h2>
            <p className="loading-message">{LOADING_MESSAGES[loadingMessageIndex]}</p>
            <div className="loading-progress">
              <div 
                className="progress-bar" 
                style={{ width: `${loadingProgress}%` }}
              ></div>
            </div>
            <div className="agent-interaction">
              <div className="agent-dots">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
              <p>Agents are collaborating...</p>
            </div>
          </div>
        </div>
      )}

      {showChat && !showLoadingScreen && showChatBox && (
        <div className="agent-chat-box">
          <button
            className="close-chatbox-btn"
            style={{position: 'absolute', top: 10, right: 10, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888', zIndex: 20}}
            onClick={() => setShowChatBox(false)}
            aria-label="Close chat"
          >
            ×
          </button>
          <div className="agent-chat-messages">
            {chat.map((msg, idx) => (
              <div key={idx} className={`agent-chat-msg ${msg.from}`}>{msg.text}</div>
            ))}
            {isLoading && (
              <div className="agent-chat-msg agent loading">
                <div className="loading-dots">
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </div>
                {statusMessage}
              </div>
            )}
            {serverStatus !== 'healthy' && (
              <div className="agent-chat-msg agent warning">
                Server status: {serverStatus === 'unavailable' ? 'Not running' : 'Unhealthy'}
              </div>
            )}
          </div>
          <form className="agent-chat-input-row" onSubmit={handleSend}>
            <input
              className="agent-chat-input"
              type="text"
              value={input}
              onChange={handleInputChange}
              placeholder={isLoading ? "Processing..." : "Type your message..."}
              disabled={isLoading || serverStatus !== 'healthy'}
              autoFocus
            />
            <button 
              className="agent-chat-send" 
              type="submit"
              disabled={isLoading || serverStatus !== 'healthy'}
            >
              {isLoading ? "..." : "Send"}
            </button>
          </form>
        </div>
      )}

      {!showChatBox && (
        <button
          className="reopen-chatbox-btn"
          style={{
            position: 'fixed',
            bottom: 30,
            right: 30,
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#2979ff',
            color: 'white',
            border: 'none',
            boxShadow: '0 2px 8px rgba(41,121,255,0.2)',
            fontSize: 28,
            cursor: 'pointer',
            zIndex: 1001
          }}
          onClick={() => setShowChatBox(true)}
          aria-label="Open chat"
        >
          💬
        </button>
      )}
    </div>
  );
};

export default DraggableAvatarCanvas; 